import React, { useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { GoPencil } from "react-icons/go";
import { BsTrash3 } from "react-icons/bs";
import AddMobileLocation from "./AddMobileLocation";
import { confirmation, errorMsg, success } from "../../../utils/notifications";
import {
  deleteMobileLocation,
  getAllMobileLocations,
} from "../../../api/mobileLocation.api";
import { RotatingLines } from "react-loader-spinner";

const MobileLocations = () => {
  const [addMobileLocationOpen, setAddMobileLocationOpen] = useState(false);
  const [mobileLocations, setMobileLocations] = useState([]);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMobileLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllMobileLocations();
      setMobileLocations(response.data || []);
    } catch (error) {
      console.error("Error fetching mobile locations:", error);
      setError("Failed to load mobile locations. Please try again.");
      setMobileLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMobileLocations();
  }, []);

  const handleDelete = async (id) => {
    confirmation("mobile location").then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteMobileLocation(id);
          setMobileLocations(mobileLocations.filter((loc) => loc._id !== id));
          success("Success!", "Mobile location deleted successfully!");
        } catch (error) {
          console.error("Error deleting mobile location:", error);

          const errorMessage =
            error?.response?.data?.message ||
            "An unexpected error occurred while deleting the mobile location.";

          errorMsg("Error!", errorMessage);
        }
      }
    });
  };

  return (
    <>
      <AddMobileLocation
        open={addMobileLocationOpen}
        setOpen={setAddMobileLocationOpen}
        fetchMobileLocations={fetchMobileLocations}
        editData={editData}
        setEditData={setEditData}
      />

      <div className="mb-5 flex justify-end sticky top-16 bg-slate-50 py-3 z-10 px-2">
        <button
          onClick={() => {
            setAddMobileLocationOpen(true);
            setEditData(null);
          }}
          className="flex items-center justify-center rounded-md bg-yellowColor px-3 py-2.5 text-sm sm:text-base font-semibold text-gray-700 shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor"
        >
          <FiPlus className="text-lg mr-1" />
          Add Mobile Location
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      ) : error ? (
        <div className="text-center font-bold text-red-600 text-xl my-4">
          {error}
        </div>
      ) : (
        <div className="mt-5 flow-root">
          {mobileLocations.length === 0 ? (
            <div className="text-center font-bold text-gray-600 text-xl my-4">
              No mobile locations added.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Name
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Identifier
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {mobileLocations.map((location) => (
                    <tr key={location._id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-base text-gray-900 sm:pl-6">
                        {location.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {location.identifier || "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            location.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {location.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-6">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setEditData(location);
                              setAddMobileLocationOpen(true);
                            }}
                            className="h-9 w-9 flex items-center justify-center bg-green-100 text-green-700 rounded-md"
                          >
                            <GoPencil />
                          </button>
                          <button
                            onClick={() => handleDelete(location._id)}
                            className="h-9 w-9 flex items-center justify-center bg-red-100 text-red-700 rounded-md"
                          >
                            <BsTrash3 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MobileLocations;
